import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import SendIcon from "@mui/icons-material/Send";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import RequestInternalNotesApi from "../../../../infrastructure/api/requestInternalNotes";
import { toJalaliFullDate } from "../../../middleware/middleware";
import FileUploader from "../../shared/uploader/FileUploader";
import MehrsanLogAttachment from "./MehrsanLogAttachment";
import { resolveOperationsSource } from "./mehrsanOperationsConstants";
import { getEntityId } from "./mehrsanOperationsUtils";

const normalizeNote = (raw) => ({
  id: getEntityId(raw),
  requestInfoId: raw.requestInfoId ?? raw.RequestInfoId,
  noteText: raw.noteText ?? raw.NoteText ?? "",
  attachmentUrl: raw.attachmentUrl ?? raw.AttachmentUrl ?? null,
  isPinned: Boolean(raw.isPinned ?? raw.IsPinned),
  createdByUserId: raw.createdByUserId ?? raw.CreatedByUserId,
  createdByFullName: raw.createdByFullName ?? raw.CreatedByFullName ?? "—",
  createOn: raw.createOn ?? raw.CreateOn,
  updateOn: raw.updateOn ?? raw.UpdateOn,
  canDelete: Boolean(raw.canDelete ?? raw.CanDelete),
});

const RequestInternalNotesTab = ({
  requestInfoId,
  detailApi,
  fileBaseUrl = "",
  active = false,
  onNotesChanged,
}) => {
  const operationsSource = resolveOperationsSource(detailApi);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [attachmentUrl, setAttachmentUrl] = useState("");

  const loadNotes = useCallback(async () => {
    if (!requestInfoId) return;
    try {
      setLoading(true);
      const res = await RequestInternalNotesApi.getByRequestId(
        operationsSource,
        requestInfoId
      );
      if (res?.isSuccess === false) {
        toast.error(res?.message || "بارگذاری یادداشت‌ها ناموفق بود");
        setNotes([]);
        return;
      }
      const list = res?.data ?? res?.Data ?? [];
      setNotes(
        (Array.isArray(list) ? list : []).map(normalizeNote).filter((n) => n.id)
      );
    } catch {
      toast.error("خطا در بارگذاری یادداشت‌های داخلی");
      setNotes([]);
    } finally {
      setLoading(false);
    }
  }, [operationsSource, requestInfoId]);

  useEffect(() => {
    if (!active || !requestInfoId) return;
    loadNotes();
  }, [active, requestInfoId, loadNotes]);

  const sortedNotes = useMemo(() => {
    const pinned = notes.filter((n) => n.isPinned);
    const rest = notes.filter((n) => !n.isPinned);
    const byDate = (a, b) =>
      new Date(b.createOn || 0).getTime() - new Date(a.createOn || 0).getTime();
    return [...pinned.sort(byDate), ...rest.sort(byDate)];
  }, [notes]);

  const handleCreate = async () => {
    const text = noteText.trim();
    if (!text) {
      toast.warning("متن یادداشت را وارد کنید");
      return;
    }
    try {
      setSubmitting(true);
      const res = await RequestInternalNotesApi.create(operationsSource, {
        requestInfoId: Number(requestInfoId),
        noteText: text,
        attachmentUrl: attachmentUrl || null,
      });
      if (res?.isSuccess === false) {
        toast.error(res?.message || "ثبت یادداشت ناموفق بود");
        return;
      }
      toast.success(res?.message || "یادداشت ثبت شد");
      setNoteText("");
      setAttachmentUrl("");
      await loadNotes();
      onNotesChanged?.();
    } catch {
      toast.error("خطا در ثبت یادداشت");
    } finally {
      setSubmitting(false);
    }
  };

  const handleTogglePin = async (note) => {
    try {
      const res = await RequestInternalNotesApi.togglePin(
        operationsSource,
        note.id
      );
      if (res?.isSuccess === false) {
        toast.error(res?.message || "تغییر سنجاق ناموفق بود");
        return;
      }
      toast.success(res?.message || "وضعیت سنجاق به‌روز شد");
      await loadNotes();
      onNotesChanged?.();
    } catch {
      toast.error("خطا در تغییر سنجاق");
    }
  };

  const handleDelete = async (note) => {
    if (!note.canDelete) {
      toast.error("شما مجاز به حذف این یادداشت نیستید");
      return;
    }
    if (!window.confirm("این یادداشت حذف شود؟")) return;
    try {
      const res = await RequestInternalNotesApi.delete(operationsSource, note.id);
      if (res?.isSuccess === false) {
        toast.error(res?.message || "حذف یادداشت ناموفق بود");
        return;
      }
      toast.success(res?.message || "یادداشت حذف شد");
      await loadNotes();
      onNotesChanged?.();
    } catch {
      toast.error("خطا در حذف یادداشت");
    }
  };

  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
        ثبت یادداشت داخلی
      </Typography>
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 1.5 }}>
        <Stack spacing={1.5}>
          <TextField
            fullWidth
            multiline
            minRows={3}
            size="small"
            label="متن یادداشت"
            placeholder="یادداشت فقط برای تیم داخلی قابل مشاهده است..."
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
          />
          <FileUploader
            moduleName="SLN"
            label="پیوست (اختیاری)"
            accept="image/*,application/pdf"
            maxFiles={1}
            baseUrl={fileBaseUrl}
            value={attachmentUrl}
            onChange={(url) => setAttachmentUrl(url || "")}
          />
          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              size="small"
              startIcon={<SendIcon />}
              onClick={handleCreate}
              disabled={submitting || !noteText.trim()}
            >
              ثبت
              {submitting ? (
                <CircularProgress size={14} color="inherit" sx={{ ml: 1 }} />
              ) : null}
            </Button>
          </Box>
        </Stack>
      </Paper>

      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
        یادداشت‌های ثبت‌شده ({notes.length})
      </Typography>

      {loading ? (
        <Box display="flex" justifyContent="center" py={3}>
          <CircularProgress size={28} />
        </Box>
      ) : sortedNotes.length === 0 ? (
        <Alert severity="info">یادداشت داخلی ثبت نشده است.</Alert>
      ) : (
        <Stack spacing={1.25}>
          {sortedNotes.map((note) => (
            <Paper
              key={note.id}
              variant="outlined"
              sx={{
                p: 1.25,
                borderRadius: 1.5,
                bgcolor: note.isPinned ? "action.hover" : "background.paper",
                borderColor: note.isPinned ? "warning.main" : "divider",
              }}
            >
              <Stack
                direction="row"
                alignItems="flex-start"
                justifyContent="space-between"
                spacing={1}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                    <Typography variant="body2" fontWeight={700}>
                      {note.createdByFullName}
                    </Typography>
                    {note.isPinned ? (
                      <PushPinIcon color="warning" sx={{ fontSize: 16 }} />
                    ) : null}
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {toJalaliFullDate(note.createOn) || "—"}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ mt: 0.75, whiteSpace: "pre-line", wordBreak: "break-word" }}
                  >
                    {note.noteText}
                  </Typography>
                  {note.attachmentUrl ? (
                    <Box sx={{ mt: 0.75 }}>
                      <MehrsanLogAttachment
                        url={note.attachmentUrl}
                        fileBaseUrl={fileBaseUrl}
                        compact
                      />
                    </Box>
                  ) : null}
                </Box>

                <Stack direction="row" spacing={0.25} flexShrink={0}>
                  <Tooltip title={note.isPinned ? "برداشتن سنجاق" : "سنجاق"}>
                    <IconButton
                      size="small"
                      color={note.isPinned ? "warning" : "default"}
                      onClick={() => handleTogglePin(note)}
                    >
                      {note.isPinned ? (
                        <PushPinIcon fontSize="small" />
                      ) : (
                        <PushPinOutlinedIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                  {note.canDelete ? (
                    <Tooltip title="حذف">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(note)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                </Stack>
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}
    </Box>
  );
};

RequestInternalNotesTab.propTypes = {
  requestInfoId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  detailApi: PropTypes.string,
  fileBaseUrl: PropTypes.string,
  active: PropTypes.bool,
  onNotesChanged: PropTypes.func,
};

export default RequestInternalNotesTab;
